// Fiche d'une démarche : à quoi elle sert, qui peut la demander, pièces, délai, coût (mention
// « tarif fictif de présentation » hors production), étapes expliquées du point de vue de l'usager,
// autorité responsable et fondement. Une seule action principale : commencer la demande.
import { STATUTS_USAGER } from '@cc/contrats';
import { EtatVide, LienBouton, Panneau } from '@cc/ui';
import { Navigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { useFicheDemarche } from '../api';
import { codeCollectiviteValide } from '../collectivite';
import { EnTeteCitoyen, Ligne, Liste } from '../commun';
import type { FicheDemarche } from '../types';
import { LIBELLES_DOMAINE } from './PageCatalogue';
import styles from '../citoyen.module.css';

/** Les sept étapes vues par l'usager, avec le texte prévu par la démarche quand il existe. */
const ETAPES_USAGER: { statut: keyof typeof STATUTS_USAGER; explication: string }[] = [
  { statut: 'enregistre', explication: 'Votre demande reçoit un numéro ; vous êtes prévenu du dépôt.' },
  { statut: 'en_verification', explication: 'Le service vérifie que le dossier est complet et les pièces lisibles.' },
  { statut: 'incomplet', explication: 'Si une pièce manque, on vous la demande ; vous la fournissez depuis votre espace.' },
  { statut: 'en_instruction', explication: 'Le service compétent examine la demande.' },
  { statut: 'en_attente_decision', explication: 'L\'autorité responsable décide dans le délai indiqué.' },
  { statut: 'approuve', explication: 'La décision vous est notifiée avec son motif ; le document délivré est disponible dans votre espace.' },
  { statut: 'termine', explication: 'La demande est clôturée puis archivée.' },
];

function Etapes({ fiche }: { fiche: FicheDemarche }) {
  const textes = new Map(fiche.textes_statuts.map((t) => [t.statut, t.texte]));
  return (
    <ol className={styles.liste}>
      {ETAPES_USAGER.map((e, i) => (
        <li key={e.statut} className={`${styles.ligne} ${styles.ligneSansChevron}`}>
          <span className={styles.ligneTexte}>
            <span className={styles.ligneTitre}>
              {i + 1}. {STATUTS_USAGER[e.statut]}
            </span>
            <span className={styles.ligneSous}>{textes.get(e.statut) ?? e.explication}</span>
          </span>
          <span />
        </li>
      ))}
    </ol>
  );
}

export function PageFicheDemarche() {
  const { code: brut, demarche } = useParams();
  const code = codeCollectiviteValide(brut);
  const fiche = useFicheDemarche(code, demarche ?? null);
  if (!code || !demarche) return <Navigate to="/citoyen" replace />;
  const f = fiche.data;
  return (
    <>
      <EnTeteCitoyen
        titre={f?.identite.libelle ?? 'Démarche'}
        surTitre={f ? `${f.collectivite.nom} · ${LIBELLES_DOMAINE[f.identite.domaine] ?? f.identite.domaine}` : undefined}
        retour={`/citoyen/${code}/demarches`}
        libelleRetour="Démarches"
        actions={
          f ? (
            <LienBouton href={`/citoyen/${code}/demarches/${demarche}/deposer`} variante="principal">
              Commencer la demande
            </LienBouton>
          ) : null
        }
      />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} fonction="La fiche de la démarche" lignes={8}>
        {f ? (
          <div className={styles.pile}>
            <Panneau titre="À quoi sert cette démarche" niveau={2}>
              <p>{f.identite.description || 'Aucune description publiée.'}</p>
              <p className={styles.aide}>
                Autorité responsable : {f.identite.autorite_responsable}. Fondement : {f.identite.fondement}. Version {f.version} de la démarche.
              </p>
            </Panneau>
            <Panneau titre="Qui peut la demander" niveau={2}>
              {f.conditions_eligibilite.texte ? <p>{f.conditions_eligibilite.texte}</p> : null}
              {f.conditions_eligibilite.regles.length > 0 ? (
                <ul>
                  {f.conditions_eligibilite.regles.map((r) => (
                    <li key={r.message}>{r.message}</li>
                  ))}
                </ul>
              ) : null}
              {!f.conditions_eligibilite.texte && f.conditions_eligibilite.regles.length === 0 ? <p>Aucune condition particulière n&apos;est publiée.</p> : null}
              {f.identite_verifiee_requise ? (
                <p className={styles.statutAttention}>Cette démarche exige une identité vérifiée au guichet avant le dépôt.</p>
              ) : (
                <p className={styles.aide}>Une identité déclarée suffit pour déposer.</p>
              )}
            </Panneau>
            <Panneau titre="Pièces à fournir" niveau={2}>
              {f.pieces.length === 0 ? (
                <p>Aucune pièce n&apos;est demandée.</p>
              ) : (
                <Liste libelle="Pièces à fournir">
                  {f.pieces.map((p) => (
                    <Ligne key={p.type} titre={p.libelle} sousTitre={p.aide ?? undefined} droite={p.conditionnelle ? 'Selon votre situation' : p.obligatoire ? 'Obligatoire' : 'Facultative'} />
                  ))}
                </Liste>
              )}
              <p className={styles.aide}>Formats acceptés : PDF, JPEG ou PNG, photo prise avec le téléphone comprise. Les pièces de votre coffre sont réutilisables.</p>
            </Panneau>
            <Panneau titre="Délai et coût" niveau={2}>
              <dl className={styles.definitions}>
                <dt>Délai de réponse</dt>
                <dd>{f.delais.reglementaire ? f.delais.reglementaire.libelle : 'Non fixé par la collectivité'}</dd>
                <dt>Si une pièce manque</dt>
                <dd>
                  Relance après {f.delais.relance_complement_jours} jours sans réponse ; classement sans suite après {f.delais.classement_sans_suite_jours} jours.
                </dd>
                <dt>Coût</dt>
                <dd>
                  {f.tarif.libelle}
                  {f.tarif.mention ? <span className={styles.aide}> ({f.tarif.mention}, aucun paiement n&apos;est demandé avant le lot L2)</span> : null}
                </dd>
              </dl>
            </Panneau>
            <Panneau titre="Les étapes de votre demande" niveau={2}>
              <Etapes fiche={f} />
              <p className={styles.aide}>Vous êtes prévenu à chaque changement : dans votre espace{f.canaux_notification.includes('sms') ? ', par SMS' : ''}{f.canaux_notification.includes('courriel') ? ', par courriel' : ''}.</p>
            </Panneau>
          </div>
        ) : (
          <EtatVide titre="Démarche introuvable" />
        )}
      </EtatRequete>
    </>
  );
}
